import type { ValidationNumericField } from "models";

export const RERUN_CONFIRM_TITLE = "Are you sure you want to rerun this?";

export const RERUN_CONFIRM_DESCRIPTION =
  "This could take several minutes. Only rerun this if you were told to, or you know something has changed in the backend and the calculations need to be redone.";

export const RERUN_HINT =
  "Re-runs all 100 scenarios against the live silver-service (~10 minutes) and overwrites the stored result. " +
  "Use this if the run failed or looks incomplete, or if admin pricing/cost data has changed since the last run " +
  "(the soil model itself never changes for an unchanged version, only the contribution-margin figures depend on pricing that can be edited independently).";

// Also used by useValidationRun to stop polling: past this the poll stops, so surface a terminal state instead of an endless spinner.
export const POLL_TIMEOUT_MS = 20 * 60 * 1000;

// ─── Compare Metrics ──────────────────────────────────────────────────────────

export type MetricDef = { key: ValidationNumericField; label: string; unit: string; higherIsBetter?: boolean; cumulative?: boolean };

export type RankedMetricDef = MetricDef & { higherIsBetter: boolean };

export const COMPARE_METRICS: RankedMetricDef[] = [
  { key: "cm_total", label: "CM Total", unit: "€/ha", higherIsBetter: true },
  { key: "cm_income", label: "CM Income", unit: "€/ha", higherIsBetter: true },
  { key: "cm_costs", label: "CM Costs", unit: "€/ha", higherIsBetter: false },
  { key: "cm_coverCropCosts", label: "CC Costs", unit: "€/ha", higherIsBetter: false },
  { key: "ss_cropYieldFractionOfTarget", label: "Yield", unit: "0–1", higherIsBetter: true },
  { key: "ss_yieldStability", label: "Stability", unit: "0–1", higherIsBetter: true },
  { key: "ss_soilAwcNormalised", label: "Water", unit: "0–1", higherIsBetter: true },
  { key: "ss_pestLossAvoided", label: "Pest", unit: "0–1", higherIsBetter: true },
  { key: "ss_nResilience", label: "N-Res", unit: "0–1", higherIsBetter: true },
  { key: "ss_leaching", label: "Leaching", unit: "0–1", higherIsBetter: false },
  { key: "ss_runoff", label: "Runoff", unit: "0–1", higherIsBetter: false },
  { key: "ss_waterErosion", label: "Water erosion", unit: "0–1", higherIsBetter: false },
  { key: "ss_windErosion", label: "Wind erosion", unit: "0–1", higherIsBetter: false },
];

// Pure function of location (GEE/soil-class baseline) — constant across rotation, scenario, and year.
export const LOCATION_SOIL_METRICS: MetricDef[] = [
  { key: "sp_rootingDepth", label: "Rooting depth", unit: "cm" },
  { key: "sp_bulkDensity", label: "Bulk density", unit: "g/cm³" },
  { key: "sp_totalCations", label: "Total cations", unit: "meq/100g" },
  { key: "sp_baseSaturation", label: "Base saturation", unit: "%" },
  { key: "sp_carbon", label: "C", unit: "%" },
  { key: "sp_totalNitrogen", label: "Total N", unit: "%" },
  { key: "sp_availableNitrogen", label: "Available N", unit: "kg/ha" },
  { key: "sp_ph", label: "pH", unit: "" },
  { key: "sp_sand", label: "Sand", unit: "%" },
  { key: "sp_silt", label: "Silt", unit: "%" },
  { key: "sp_clay", label: "Clay", unit: "%" },
  { key: "sp_microbialBiomass", label: "Microbial biomass", unit: "mg/kg" },
  { key: "sp_airTemperature", label: "Air temp", unit: "°C" },
  { key: "sp_rainfall", label: "Rainfall", unit: "mm" },
  { key: "sp_slope", label: "Slope", unit: "%" },
  { key: "sp_wind", label: "Wind", unit: "m/s" },
  { key: "sp_structure", label: "Structure", unit: "0–1" },
  { key: "sp_earthwormDensity", label: "Earthworm density", unit: "/m²" },
  { key: "tillage_porosity", label: "Tillage porosity", unit: "0–1" },
  { key: "sp_surfaceResidues", label: "Surface residues", unit: "0–1" },
  { key: "sp_weedSeedDensity", label: "Weed seed density", unit: "/m²" },
  { key: "sp_surfaceSealing", label: "Surface sealing", unit: "0–1" },
  // v5-only: no v3/v4 equivalent.
  { key: "sp_nutrientAvailability", label: "Nutrient availability", unit: "0–1" },
  { key: "sp_biodiversity", label: "Biodiversity", unit: "0–1" },
  { key: "sp_nutrientCycling", label: "Nutrient cycling", unit: "0–1" },
];

// Function of location + the rotation's first crop — constant across scenario and year, but varies by rotation.
export const CROP_SOIL_METRICS: MetricDef[] = [
  { key: "sp_cropNRequirement", label: "Crop N req.", unit: "kg/ha" },
  { key: "sp_cropPhPreference", label: "Crop pH pref.", unit: "" },
  { key: "sp_cropGrowingDegreeDays", label: "Crop GDD", unit: "°C·d" },
  { key: "sp_cropMoistureRequirements", label: "Crop moisture", unit: "mm" },
  { key: "sp_cropRootingDepth", label: "Crop root depth", unit: "cm" },
];

export const SOIL_PROPERTY_METRICS: MetricDef[] = [...LOCATION_SOIL_METRICS, ...CROP_SOIL_METRICS];

// Where each sp_* soil property value actually comes from — differs by version, not just by metric:
// v3/v4 fetch the geospatial fields from a real Google Earth Engine call inside silver-service;
// v5 has no GEE call at all, so the same fields (where available) come from the reference-data/soil
// endpoint instead (see mergeReferenceSoil in the API), and the rest are the model's own output.
export type SoilPropertyOrigin = "model" | "gee" | "reference-data" | "unavailable";

export const SOIL_PROPERTY_ORIGIN_LABEL: Record<SoilPropertyOrigin, string> = {
  model: "Model",
  gee: "GEE",
  "reference-data": "Reference data",
  unavailable: "Unavailable",
};

export const SOIL_PROPERTY_ORIGIN_STYLE: Record<SoilPropertyOrigin, { background: string; color: string }> = {
  model: { background: "#e0f2fe", color: "#0369a1" },
  gee: { background: "#dcfce7", color: "#16a34a" },
  "reference-data": { background: "#fef9c3", color: "#a16207" },
  unavailable: { background: "#f3f4f6", color: "#6b7280" },
};

export const VALIDATION_LOCATION_COLORS = ["#0ea5e9", "#f59e0b", "#10b981", "#f43f5e", "#8b5cf6"];

export const VALIDATION_SCENARIO_COLORS: Record<string, string> = {
  Baseline: "#6b7280",
  "No-till": "#16a34a",
  "Winter CC": "#2563eb",
  "Summer CC": "#ea580c",
  Stacked: "#7c3aed",
};

export const VALIDATION_DEFAULT_SCENARIO_COLORS = ["#0891b2", "#be185d", "#b45309", "#4f46e5", "#059669"];
